import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const eventTarget = event.target;
    if (eventTarget instanceof Element && eventTarget.closest('product-bundle-list-buy-buttons')) return;
    if (eventTarget instanceof Element && eventTarget.closest('product-bundle-buy-buttons')) return;
    if (eventTarget instanceof Element && eventTarget.closest('product-upsell-buy-buttons')) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * @param {number | string} index
   */
  selectPrimaryThumbnail(index) {
    const i = typeof index === 'number' ? index : parseInt(String(index), 10);
    if (Number.isNaN(i)) return;

    this.querySelectorAll('[data-primary-media-index]').forEach((el) => {
      const idx = parseInt(el.getAttribute('data-primary-media-index') || '0', 10);
      if (idx === i) {
        el.removeAttribute('hidden');
        el.classList.remove('is-primary-pane-hidden');
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
      } else {
        el.setAttribute('hidden', '');
        el.classList.add('is-primary-pane-hidden');
      }
    });

    const thumbs = this.refs.primaryThumbnail;
    const list = Array.isArray(thumbs) ? thumbs : thumbs ? [thumbs] : [];
    list.forEach((btn, idx) => {
      const on = idx === i;
      btn.setAttribute('aria-current', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
    });
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
